import _ from 'lodash'
import { vals } from '../util'
import store from '../store'
import moment from 'moment'
import { round } from 'd3'
// company as defined in mongoose/mongo
/**
 * Company
 * profile: mongo _id of the user's profile
 * ucid: unique company id (public)
 * name: name of the company (unique to the profile)
 * status: company's status (active, ipo, etc)
 * last_funding: date of last round of funding
 * stage: current state of company (pre-seed, seed, ipo, etc)
 * sector: which sector the company is in
 * geography: physical location of the company
 * description: loose description of the company
 * logo_url: image (url) string
 * modified: date stamp of last time it was modified in mongo
 * rating: rating as dictated by user
 */
// Validation items
const REQUIRED_FIELDS = ['name']
const TYPE_DEFS = {
  name: String, status: String, stage: String, sector: String, current_stage: String,
  geography: String, description: String, logo_url: String, rating: Number, contacts: String, contact_email: String,
  data_status: String,
  founding_date: String,
  company_type: String,
  category_data: String,
  geography: String,
  headquarters: String,
  employee_count: String,
  website: String,
  facebook: String,
  linkedin: String,
  twitter: String,
  contact_email: String,
  estimated_revenue_range: String,
  ipo_status: String,
  funding_rounds: Object,
  permalink: String,
  direct: String, direct_lp: String, initial_investment: [Date, String],
  context: String, notes: String, cost: Number, value: Number, ownership: Number,
  last_funding_round: [Date, String], stage_i_invested: String, fund: String, pro_rata_rights: Boolean,
  referred_by: Array,
  referred_by_name: String,
  referred_by_email: String,
  ceo_name: String,
  ceo_email: String,
  tags: Array,
  deal_stage: String,
  company_status: String,
  todo_remainder: String,
  investor_stage: String,
  fund_investment_date: [Date, String],
  ipo_info: Object,
  acquisition_info: Object,
  acquired_company_info: Object
}
const ENUM_DEFS = { sector: vals.sectorList, current_stage: vals.STAGE_DEFINES, stage: vals.STAGE_DEFINES, status: vals.STATUS_DEFINES, investor_stage: vals.stageDropdown }

export class Company {
  constructor({ _id = undefined, __v = undefined, profile = undefined, ucid = undefined, name = undefined, status = undefined, stage = undefined, sector = undefined, geography = undefined,
    description = undefined, logo_url = undefined, modified = (moment()), rating = undefined, contacts = undefined, contact_email = undefined, data_status = 'Unconfirmed', permalink = undefined, ipo_status = undefined,
    estimated_revenue_range = undefined, direct = undefined, direct_lp = undefined, initial_investment = undefined,
    context = undefined, notes = undefined, cost = undefined, value = undefined, ownership = undefined,
    last_funding_round = undefined, round = undefined, stage_i_invested = undefined, fund = undefined, pro_rata_rights = undefined, referred_by = undefined, referred_by_name = undefined, referred_by_email = undefined,
    ceo_name = undefined, ceo_email = undefined, tags = undefined, deal_stage = undefined, company_status = undefined, current_stage = undefined, todo_remainder = undefined } = {}, investor_stage = undefined, fund_investment_date = undefined, ipo_info = undefined, acquisition_info = undefined, acquired_company_info = undefined) {
    //#region Mongo internal
    this.__v = __v
    this._id = _id
    this.modified = modified
    this.ucid = ucid
    this.profile = profile
    //#endreigon
    this.name = name
    this.current_stage = current_stage;
    this.status = status
    this.stage = stage
    this.sector = sector
    this.geography = geography
    this.description = description
    this.logo_url = logo_url
    this.rating = rating
    this.contacts = contacts // placeholder

    this.data_status = data_status;
    this.contact_email = contact_email;
    this.permalink = permalink;
    this.company_status = company_status;

    this.ipo_status = ipo_status;
    this.estimated_revenue_range = estimated_revenue_range;
    this.direct = direct;
    this.direct_lp = direct_lp;
    this.initial_investment = initial_investment;
    this.context = context;
    this.notes = notes;
    this.cost = cost;
    this.value = value;
    this.ownership = ownership;
    this.last_funding_round = last_funding_round;
    this.stage_i_invested = stage_i_invested;
    this.fund = fund;
    this.pro_rata_rights = pro_rata_rights;
    this.data_status = data_status;
    this.referred_by = referred_by;
    this.referred_by_name = referred_by_name;
    this.referred_by_email = referred_by_email;
    this.ceo_name = ceo_name;
    this.ceo_email = ceo_email;
    this.deal_stage = deal_stage;
    this.tags = tags;
    this.todo_remainder = todo_remainder;
    this.investor_stage = investor_stage;
    this.fund_investment_date = fund_investment_date;
    this.ipo_info = ipo_info;
    this.acquisition_info = acquisition_info;
    this.acquired_company_info = acquired_company_info;
  }

  /**
   * Update the company object with new information
   * @param {object} obj with fields to be written
   * @returns {boolean} T/F if the company was updated
   */
  update(obj) {
    if (!_.isNil(obj) && !_.isEmpty(obj)) {
      // do a merge with this to update the company info
      _.forIn(TYPE_DEFS, (v, k) => {
        if (!_.isNil(obj[k])) {
          this[k] = _.cloneDeep(obj[k])
        }
      })
      return true
    }
    return false
  }

  /**
   * Run validation on all of the company object's fields. Making sure the type and required fields are set correctly.
   * @returns {Promise} resolve: no return (empty object), reject: array of validation errors
   */
  validate() {
    return new Promise((resolve, reject) => {
      // validate this company construct with some rules
      let retval = { errors: [], warnings: [], type: 'company' }
      // go through and check required fields
      let reqErrors = _.map(REQUIRED_FIELDS, field => {
        if (_.isArray(field)) {
          // this is an 'or' requirement, any one of these must be present
          let or = _.reduce(field, (m, ors) => {
            return m || (_.isNil(this[ors]) || _.isEmpty(this[ors]))
          }, false)
          if (or) return `Missing one of: ${JSON.stringify(field)}`
          return null
        }
        if (_.isNil(this[field]) || _.isEmpty(this[field])) return `Missing required field: ${field}`
        return null
      })
      reqErrors = _.without(reqErrors, null, undefined)
      retval.errors = _.concat(retval.errors, reqErrors)
      // go through and check valid types for fields
      let validErrors = _.map(TYPE_DEFS, (type, field) => {
        if (!_.isArray(type) && !_.isNil(this[field])) {
          if (type === Date) {
            // need to check for moments
            if (this[field].constructor !== type && !moment.isMoment(this[field])) return `Invalid type for field: ${field}`
          } else {
            if (this[field].constructor !== type) return `Invalid type for field: ${field}`
          }
          // if (this[field].constructor !== type) return `Invalid type for field: ${field}`
        } else if (!_.isNil(this[field])) {
          // can be of any type in the array
          // let isValid = _.reduce(type, (m, t) => {
          //   return m || this[field].constructor === t
          // }, false)
          // if (!isValid) return `Invalid type for field: ${field}`
          let isValid = _.reduce(type, (m, t) => {
            // need to check if we are comparing a Date to Moment
            if (t === Date) {
              return m || (this[field].constructor === t) || moment.isMoment(this[field])
            } else {
              return m || this[field].constructor === t
            }
          }, false)
          if (!isValid) return `Invalid type for field: ${field}`
        }
        return null
      })
      validErrors = _.without(validErrors, null, undefined)
      retval.errors = _.concat(retval.errors, validErrors)
      // check enums for truthy
      let enumErrors = _.map(ENUM_DEFS, (vals, field) => {
        if (!_.isNil(this[field])) {
          // check to see that the field is a valid value
          if (_.indexOf(vals, this[field]) < 0) return `Invalid value for field: ${field}`
        }
        return null
      })
      enumErrors = _.without(enumErrors, null, undefined)
      retval.errors = _.concat(retval.errors, enumErrors)
      if (retval.errors.length > 0) return reject(retval)
      return resolve(retval)
    })
  }

  /**
   * Writes the company object to the server, doing a patch/post.
   * @returns {Promise} resovle: the new/updated company object, reject: error w/ text
   */
  writeToServer() {
    // do 1 of 2 things depending on our data
    // 1, if we don't have _id, we need to post
    // 2, we do have _id (and assuming the rest), do a patch
    let path = (_.isNil(this._id) || _.isEmpty(this._id)) ? 'company/apiPostCompanyInst' : 'company/apiPatchCompanyInst'
    return store.dispatch(path, this)
      .then(res => {
        if (res.success.length > 0) {
          res = res.success[0]
          // update our info...
          _.assignIn(this, res)
          return Promise.resolve(this)
        } else {

          let msg = 'Unable to create/update this company.';
          if (res.fail && res.fail[0]
            && res.fail[0].error
            && res.fail[0].error.message) {
            msg = res.fail[0].error.message;
          }
          return Promise.reject({ error: msg });
        }
      })
      .catch(err => {
        return Promise.reject(err)
      })
  }


  writeToDiligenceServer() {
    // do 1 of 2 things depending on our data
    // 1, if we don't have _id, we need to post
    // 2, we do have _id (and assuming the rest), do a patch
    let path = (_.isNil(this._id) || _.isEmpty(this._id)) ? 'diligence/apiPostDiligenceInst' : 'diligence/apiPatchDiligenceInst'
    return store.dispatch(path, this)
      .then(res => {
        if (res.success.length > 0) {
          res = res.success[0]
          // update our info...
          _.assignIn(this, res)
          return Promise.resolve(this)
        } else {

          let msg = 'Unable to create/update this company.';
          if (res.fail && res.fail[0]
            && res.fail[0].error
            && res.fail[0].error.message) {
            msg = res.fail[0].error.message;
          }
          return Promise.reject({ error: msg });
        }
      })
      .catch(err => {
        return Promise.reject(err)
      })
  }
}

/**
 * Create a company instance with base-level company data needed for a patch/update
 * @param {String} id (ucid or _id)
 */
export function patchCompany(id) {
  if (_.isNil(id) || _.isEmpty(id)) {
    return undefined
  }
  // create a new company by finding the company from our company store
  let data = store.getters['portfolio/getPortfolio'](id)
  // get only base data needed for update
  data = _.pick(data, ['_id', '__v', 'ucid'])
  return new Company(data)
}

export function createCompany(data) {
  return new Company(data)
}